'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useResizable } from './use-resizable';
import { COLLAPSED_ASIDE_WIDTH } from '@/components/ui/shell';
interface UseShellChatAsideOptions {
  /** Shared width persistence — matches Study Design shell behavior. */
  widthStorageKey?: string;
  defaultWidth?: number;
  /**
   * When set, collapse state is owned here (and persisted) so Shell can
   * dismiss the drawer via backdrop / Escape without fighting ChatPanel.
   * Pass the same key ChatPanel used to own (`crucible-chat-collapsed-*`).
   */
  collapseStorageKey?: string;
  /**
   * Initial collapse when nothing is stored yet. Hub defaults collapsed so
   * the landing viewport isn't squeezed on first visit.
   */
  defaultCollapsed?: boolean;
}
function readCollapsed(key: string | undefined, fallback: boolean): boolean {
  if (typeof window === 'undefined' || !key) return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    // Absent key → honor defaultCollapsed; only an explicit value overrides.
    return stored === null ? fallback : stored === 'true';
  } catch {
    return fallback;
  }
}

/**
 * Shell chat aside sizing — resize handle + collapse-to-48px strip.
 * Keeps Shell `asideWidth` in sync with ChatPanel collapse state.
 */
export function useShellChatAside(t0) {
  const $ = _c(22);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    widthStorageKey: t2,
    defaultWidth: t3,
    collapseStorageKey,
    defaultCollapsed: t4
  } = t1;
  const widthStorageKey = t2 === undefined ? "crucible-chat-width" : t2;
  const defaultWidth = t3 === undefined ? 440 : t3;
  const defaultCollapsed = t4 === undefined ? false : t4;
  const [chatCollapsed, setChatCollapsed] = useState(defaultCollapsed);
  let t5;
  let t6;
  if ($[2] !== collapseStorageKey || $[3] !== defaultCollapsed) {
    t5 = () => {
      setChatCollapsed(readCollapsed(collapseStorageKey, defaultCollapsed));
    };
    t6 = [collapseStorageKey, defaultCollapsed];
    $[2] = collapseStorageKey;
    $[3] = defaultCollapsed;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] !== collapseStorageKey) {
    t7 = next => {
      setChatCollapsed(next);
      if (!collapseStorageKey) {
        return;
      }
      try {
        window.localStorage.setItem(collapseStorageKey, String(next));
      } catch {}
    };
    $[6] = collapseStorageKey;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const updateCollapsed = t7;
  let t8;
  if ($[8] !== defaultWidth || $[9] !== widthStorageKey) {
    t8 = {
      defaultWidth,
      minWidth: 320,
      maxWidthRatio: 0.4,
      storageKey: widthStorageKey,
      direction: "left"
    };
    $[8] = defaultWidth;
    $[9] = widthStorageKey;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const {
    width: chatPanelWidth,
    isResizing: isChatResizing,
    startResize: startChatResize
  } = useResizable(t8);
  const asideWidth = chatCollapsed ? COLLAPSED_ASIDE_WIDTH : chatPanelWidth;
  const onCollapsedChange = updateCollapsed;
  let t9;
  if ($[11] !== updateCollapsed) {
    t9 = () => {
      updateCollapsed(true);
    };
    $[11] = updateCollapsed;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const onAsideClose = t9;
  const t10 = collapseStorageKey ? chatCollapsed : undefined;
  let t11;
  if ($[13] !== asideWidth || $[14] !== chatCollapsed || $[15] !== chatPanelWidth || $[16] !== isChatResizing || $[17] !== onAsideClose || $[18] !== onCollapsedChange || $[19] !== startChatResize || $[20] !== t10) {
    t11 = {
      asideWidth,
      chatPanelWidth,
      isChatResizing,
      startChatResize,
      chatCollapsed,
      collapsed: t10,
      onCollapsedChange,
      onAsideClose
    };
    $[13] = asideWidth;
    $[14] = chatCollapsed;
    $[15] = chatPanelWidth;
    $[16] = isChatResizing;
    $[17] = onAsideClose;
    $[18] = onCollapsedChange;
    $[19] = startChatResize;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}